export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value);

export function isPlainRecord(
  value: unknown
): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export function hasOnlyKeys(
  value: Record<string, unknown>,
  allowed: readonly string[]
): boolean {
  return Object.keys(value).every((key) =>
    allowed.some((candidate) => candidate === key)
  );
}

export function property(value: unknown, key: PropertyKey): unknown {
  if (value === null || typeof value !== "object") return undefined;
  return Reflect.get(value, key);
}

/** A non-empty string, or `undefined` for anything else (including `""`). */
export function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function arrayProperty(
  value: unknown,
  key: PropertyKey
): unknown[] | undefined {
  const result = property(value, key);
  return Array.isArray(result) ? result : undefined;
}

export function optionalStringProperty(
  value: unknown,
  key: PropertyKey
): string | undefined | null {
  const result = property(value, key);
  if (result === undefined) return undefined;
  return asNonEmptyString(result) ?? null;
}

export function firstStringProperty(
  value: unknown,
  keys: readonly PropertyKey[]
): string | undefined {
  for (const key of keys) {
    const result = optionalStringProperty(value, key);
    if (result) return result;
  }
  return undefined;
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}
